import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SupabaseService } from '../../supabase/supabase.service';
import { SecurityService } from '../../services/security.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  email = signal('');
  password = signal('');
  loading = signal(false);
  errorMessage = signal('');
  emailError = signal('');
  passwordError = signal('');

  constructor(private supabaseService: SupabaseService, private router: Router, private securityService: SecurityService) {}

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    return this.securityService.isValidEmail(email);
  }

  /**
   * Validate form inputs
   */
  private validateForm(): boolean {
    this.emailError.set('');
    this.passwordError.set('');
    let isValid = true;

    // Validate email
    const emailValue = this.email().trim();
    if (!emailValue) {
      this.emailError.set('Email is required');
      isValid = false;
    } else if (!this.isValidEmail(emailValue)) {
      this.emailError.set('Please enter a valid email address');
      isValid = false;
    } else if (emailValue.length > 255) {
      this.emailError.set('Email is too long');
      isValid = false;
    }

    // Validate password
    const passwordValue = this.password();
    if (!passwordValue) {
      this.passwordError.set('Password is required');
      isValid = false;
    } else if (passwordValue.length < 6) {
      this.passwordError.set('Password must be at least 6 characters');
      isValid = false;
    } else if (passwordValue.length > 128) {
      this.passwordError.set('Password is too long');
      isValid = false;
    }

    return isValid;
  }

  async onSubmit() {
    // Validate form first
    if (!this.validateForm()) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    // Trim whitespace from email
    const trimmedEmail = this.email().trim();

    const { data, error } = await this.supabaseService.signIn(trimmedEmail, this.password());
    
    if (error) {
      this.errorMessage.set(error.message || 'Login failed');
      console.error('Login error:', error);
    } else {
      // Check if user is admin and redirect accordingly
      const user = data.user;
      const role = (user as any)?.user_metadata?.role;
      if (role === 'admin') {
        this.router.navigate(['/admin']);
      } else {
        this.router.navigate(['/home']);
      }
    }

    this.loading.set(false);
  }
}
